import React, {useEffect} from 'react';
import {Platform} from 'react-native';
import {WebView} from 'react-native-webview';
import {Routes} from '../../routing/model';

type EsiaLogoutListener = (link: string) => void;

let queuedEsiaLogoutLink: string | null = null;
const esiaLogoutListeners = new Set<EsiaLogoutListener>();

/** Queue ESIA logout for CardReissue so buttons stay disabled until redirect. */
export const queueEsiaLogoutForCardReissue = (link: string) => {
  if (esiaLogoutListeners.size === 0) {
    queuedEsiaLogoutLink = link;
    return;
  }
  queuedEsiaLogoutLink = null;
  esiaLogoutListeners.forEach((listener) => listener(link));
};

export const subscribeEsiaLogoutForCardReissue = (listener: EsiaLogoutListener) => {
  esiaLogoutListeners.add(listener);
  if (queuedEsiaLogoutLink) {
    const link = queuedEsiaLogoutLink;
    queuedEsiaLogoutLink = null;
    listener(link);
  }
  return () => {
    esiaLogoutListeners.delete(listener);
  };
};

export const openEsiaLogoutInBrowser = (link: string) => {
  if (typeof window === 'undefined') return;

  const goSameTab = () => {
    try {
      const url = new URL(link);
      url.searchParams.set('redirect_url', `${window.location.origin}${Routes.cardReissue}`);
      window.location.assign(url.toString());
    } catch {
      window.location.assign(link);
    }
  };

  setTimeout(goSameTab, 2000);
};

export const EsiaLogoutWebView = ({
  link,
  onLoadEnd,
  onError,
}: {
  link: string | null;
  onLoadEnd?: () => void;
  onError?: () => void;
}) => {
  useEffect(() => {
    if (!link || Platform.OS !== 'web') return;
    openEsiaLogoutInBrowser(link);
    onLoadEnd?.();
  }, [link]);

  if (Platform.OS === 'web' || !link) {
    return null;
  }

  const isOldAndroid = Platform.OS === 'android' && typeof Platform.Version === 'number' && Platform.Version < 29;

  return (
    <WebView
      pointerEvents='none'
      style={{position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, opacity: 0}}
      source={{uri: link}}
      onLoadEnd={onLoadEnd}
      onError={onError}
      androidHardwareAccelerationDisabled
      androidLayerType={isOldAndroid ? 'software' : undefined}
      thirdPartyCookiesEnabled
      cacheEnabled={false}
    />
  );
};
